import { A11yModule } from '@angular/cdk/a11y';
import { OverlayRef } from '@angular/cdk/overlay';

import {
  ChangeDetectionStrategy,
  Component,
  InjectionToken,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  computed,
  inject,
  model,
  output,
  signal
} from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MODAL_CONFIG, ModalRef } from './modal.service';
import { ButtonComponent, modalBodyAnimation, modalBackdropAnimation } from '@infinitform/shared-ui';

export interface ModalAction {
  label: string;
  action: 'close' | 'cancel' | 'save' | 'custom';
  customAction?: string;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
}

export interface ModalData {
  title?: string;
  content?: string;
  actions?: ModalAction[];
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'material-modal';
  dismissible?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
  topClose?: boolean;
  customClass?: string;
}

export interface ModalResult {
  action: string;
  data?: any;
}

export class TemplateModalOverlayRef implements ModalRef {
  public id: string;
  public componentInstance?: any;

  private _afterClosed = new BehaviorSubject<any>(null);
  private _afterOpened = new BehaviorSubject<any>(null);
  private _backdropClick = new BehaviorSubject<MouseEvent | null>(null);
  private _keydownEvents = new BehaviorSubject<KeyboardEvent | null>(null);
  private readonly _isClosing = signal(false);
  readonly isClosing = this._isClosing.asReadonly();
  private _closeResult: any;
  private _disposed = false;

  constructor(
    private overlayRef: OverlayRef,
    id?: string
  ) {
    this.id = id || `modal-${Date.now()}`;

    // Set up event subscriptions
    this.overlayRef.backdropClick().subscribe((event) => {
      this._backdropClick.next(event);
    });

    this.overlayRef.keydownEvents().subscribe((event) => {
      this._keydownEvents.next(event);
    });

    // Emit opened event
    setTimeout(() => this._afterOpened.next(null));
  }

  close(result?: any): void {
    if (this._disposed || this._isClosing()) return;
    this._closeResult = result;
    this._isClosing.set(true);
    // Safety fallback: dispose if animation done callback doesn't fire
    setTimeout(() => this.finishClose(), 300);
  }

  finishClose(): void {
    if (this._disposed) return;
    this._disposed = true;
    this._afterClosed.next(this._closeResult);
    this.overlayRef.dispose();
  }

  afterClosed(): Observable<any> {
    return this._afterClosed.asObservable();
  }

  afterOpened(): Observable<any> {
    return this._afterOpened.asObservable();
  }

  backdropClick(): Observable<MouseEvent> {
    return this._backdropClick.asObservable().pipe(filter((event) => event !== null)) as Observable<MouseEvent>;
  }

  keydownEvents(): Observable<KeyboardEvent> {
    return this._keydownEvents.asObservable().pipe(filter((event) => event !== null)) as Observable<KeyboardEvent>;
  }
}

export const DIALOG_DATA = new InjectionToken<any>('DIALOG_DATA');

@Component({
    selector: 'app-modal',
    imports: [A11yModule, ButtonComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './modal.component.html',
    styleUrl: './modal.component.scss',
    encapsulation: ViewEncapsulation.None,
    animations: [
        modalBodyAnimation(),
        modalBackdropAnimation()
    ]
})
export class ModalComponent implements OnInit, OnDestroy {
  data = model<ModalData>({
    title: '',
    content: '',
    actions: [],
    size: 'md',
    dismissible: true,
    showHeader: true,
    showFooter: true,
    topClose: true
  });

  modalResult = output<ModalResult>();
  closed = output<void>();

  private overlayRef = inject(TemplateModalOverlayRef, { optional: true });
  private modalConfig = inject(MODAL_CONFIG, { optional: true });

  modalWidth = computed(() => {
    // Raw `width` (eg '560px', '70vw') wins over the `size` token.
    if (this.modalConfig?.width) return this.modalConfig.width;

    const size = this.data()?.size;
    const configSize = this.modalConfig?.size;
    const effectiveSize = configSize || size;

    switch (effectiveSize) {
      case 'sm':
        return '400px';
      case 'md':
        return '500px';
      case 'lg':
        return '700px';
      case 'xl':
        return '900px';
      case 'full':
        return '95vw';
      case 'material-modal':
        return '70%';
      default:
        return 'auto';
    }
  });

  modalHeight = computed(() => {
    if (this.modalConfig?.height) return this.modalConfig.height;

    const size = this.data()?.size;
    const configSize = this.modalConfig?.size;
    const effectiveSize = configSize || size;

    switch (effectiveSize) {
      case 'full':
        return '95vh';
      case 'material-modal':
        return '70%';
      default:
        return 'auto';
    }
  });

  modalSizeClasses = computed(() => {
    const size = this.data()?.size;
    const configSize = this.modalConfig?.size;
    const effectiveSize = configSize || size;
    const baseClasses = 'w-full';

    switch (effectiveSize) {
      case 'sm':
        return `${baseClasses} max-w-md`;
      case 'md':
        return `${baseClasses} max-w-lg`;
      case 'lg':
        return `${baseClasses} max-w-2xl`;
      case 'xl':
        return `${baseClasses} max-w-4xl`;
      case 'full':
        return `${baseClasses} max-w-[95vw]`;
      case 'material-modal':
        return `${baseClasses} max-w-[600px]`;
      default:
        return `${baseClasses} max-w-lg`;
    }
  });

  modalClass = computed(() => {
    const size = this.data()?.size;
    const configSize = this.modalConfig?.size;
    const effectiveSize = configSize || size;
    return effectiveSize;
  });

  showCloseButton = computed(() => {
    return !this.modalConfig?.hideCloseButton;
  });

  animState = computed(() => this.overlayRef?.isClosing() ? 'closing' : 'open');

  ngOnInit(): void {
    // Set default actions if none provided
    if (!this.data().actions || this.data().actions!.length === 0) {
      this.data.update((data) => ({
        ...data,
        actions: [
          { label: 'Cancel', action: 'cancel', variant: 'secondary' },
          { label: 'Save', action: 'save', variant: 'primary' }
        ]
      }));
    }

    // Handle escape key
    document.addEventListener('keydown', this.handleEscapeKey);
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.handleEscapeKey);
  }

  private handleEscapeKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.data().dismissible !== false) {
      this.handleAction('close');
    }
  };

  onBackdropClick(event: MouseEvent): void {
    if (this.data().dismissible !== false && event.target === event.currentTarget) {
      this.handleAction('close');
    }
  }

  handleAction(action: string, customAction?: string): void {
    const resultAction = customAction || action;
    this.modalResult.emit({ action: resultAction });

    if (action === 'close' || action === 'cancel' || action === 'save') {
      this.close();
    }
  }

  hasActions(): boolean {
    return !!this.data().actions && this.data().actions!.length > 0;
  }

  getActionClasses(action: ModalAction): string {
    const variant = action.variant || 'secondary';
    return `btn btn-${variant}`;
  }

  close(): void {
    this.closed.emit();
    this.overlayRef?.close();
  }

  onBodyAnimationDone(event: { toState: string }): void {
    if (event.toState === 'closing') {
      this.overlayRef?.finishClose();
    }
  }
}
